import React, { useState } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery } from 'react-query';
import { salesCyclesApi } from '../services/api';
import { useChartTheme } from '../hooks/useChartTheme';

interface TrendsData {
  period_start: string;
  period_label: string;
  period_type: string;
  total_deals: number;
  scored_deals: number;
  avg_score: number;
  total_amount: number;
  _unique_totals?: {
    unique_total_deals: number;
    unique_scored_deals: number;
    unique_total_amount: number;
  };
}

interface TrendsChartProps {
  selectedCycleTypes?: string[];
  selectedAccountValues?: string[];
  selectedSalesRep?: string | null;
  selectedTeam?: string | null;
}

type PeriodType = 'week' | 'month' | 'quarter';

const TrendsChart: React.FC<TrendsChartProps> = ({
  selectedCycleTypes,
  selectedAccountValues,
  selectedSalesRep,
  selectedTeam
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('month');
  const chartTheme = useChartTheme();

  // Smart score formatting: round to nearest integer if within 0.03 of a whole number
  const formatScore = (score: number): string => {
    const rounded = Math.round(score);
    // If the score is within 0.03 of a whole number, show the whole number
    // This catches cases like 0.98, 0.99 -> 1.0 and 1.97, 1.98 -> 2.0
    if (Math.abs(score - rounded) <= 0.03) {
      return rounded.toFixed(1); // Shows 1.0, 2.0, etc.
    }
    // Otherwise keep decimal precision
    return score.toFixed(2);
  };

  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem('salesai-api-key');

  // Convert cycle types array to comma-separated string for API calls
  const cycleTypesString = selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined;

  const { data: trendsData, isLoading, error, isFetching } = useQuery<TrendsData[]>(
    [
      'dealScoringTrends',
      selectedPeriod,
      cycleTypesString || 'all',
      selectedAccountValues?.join(','),
      selectedSalesRep,
      selectedTeam
    ],
    () => salesCyclesApi.getDealScoringTrends(
      selectedPeriod,
      cycleTypesString,
      selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
      selectedSalesRep,
      selectedTeam
    ),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      enabled: isAuthenticated
    }
  );

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-gray-900 dark:text-gray-100 font-semibold font-inter">{`${data.period_type} ${label}`}</p>
          <div className="space-y-2 mt-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400 font-inter">Average Score: </span>
              <span className="text-sm font-semibold text-purple-600 dark:text-purple-400 font-inter">{formatScore(data.avg_score)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400 font-inter">Open Deals in the Period: </span>
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 font-inter">{data.total_deals}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 font-inter">
              <div>Scored Deals: {data.scored_deals}</div>
              <div>Total Amount: ${data.total_amount.toLocaleString()}</div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const periodOptions = [
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' }
  ] as const;

  if (isLoading) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <div className="text-gray-700 dark:text-gray-300 text-sm font-inter font-medium">Loading trends data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-600 dark:text-red-400 text-lg font-semibold font-inter mb-2">Error loading trends</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">Please try again later</div>
          </div>
        </div>
      </div>
    );
  }

  if (!trendsData || trendsData.length === 0) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Deal Scoring Trends</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm font-inter">Average deal scores over time</p>
          </div>
          
          {/* Period Selector */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedPeriod(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors font-inter ${
                  selectedPeriod === option.value
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-gray-900 dark:text-gray-100 text-lg font-semibold font-inter mb-2">No trends data available</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">Try adjusting your filters or time period</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
      {/* Header with Period Selector */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Deal Scoring Trends</h3>
            {isFetching && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
            )}
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-1">
            Shows the average total score of all open deals per period, calculated by summing all 11 exit criteria completed for each deal and averaging across scored deals • A deal is counted if created before period end and either still open or closed after period start • {trendsData.length} periods shown
          </p>
        </div>
        
        {/* Period Selector */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {periodOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedPeriod(option.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors font-inter ${
                selectedPeriod === option.value
                  ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={trendsData}
            margin={{
              top: 20,
              right: 50,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridStroke} />
            <XAxis
              dataKey="period_label"
              stroke={chartTheme.axisStroke}
              fontSize={12}
              fontFamily="Inter, sans-serif"
              tick={{ fill: chartTheme.tickFill }}
            />
            {/* Left Y-axis for Average Score */}
            <YAxis
              yAxisId="score"
              stroke={chartTheme.axisStroke}
              fontSize={12}
              fontFamily="Inter, sans-serif"
              tick={{ fill: chartTheme.tickFill }}
              domain={[0, (dataMax: number) => Math.max(1, Math.ceil(dataMax + 0.2))]}
              tickCount={6}
            />
            {/* Right Y-axis for Deal Count */}
            <YAxis
              yAxisId="deals"
              orientation="right"
              stroke={chartTheme.axisStroke}
              fontSize={12}
              fontFamily="Inter, sans-serif"
              tick={{ fill: chartTheme.tickFill }}
              domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
              tickCount={6}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
            />
            {/* Primary line: Average Score */}
            <Line
              yAxisId="score"
              type="monotone"
              dataKey="avg_score"
              stroke="#9333ea"
              strokeWidth={3}
              dot={{ fill: '#9333ea', strokeWidth: 2, r: 5 }}
              activeDot={{ r: 7, stroke: '#9333ea', strokeWidth: 2, fill: '#ffffff' }}
              name="Average Score"
              connectNulls={false}
            />
            {/* Secondary line: Open Deals in Period */}
            <Line
              yAxisId="deals"
              type="monotone"
              dataKey="total_deals"
              stroke="#9ca3af"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={{ fill: '#9ca3af', strokeWidth: 1, r: 3 }}
              activeDot={{ r: 5, stroke: '#9ca3af', strokeWidth: 1, fill: '#ffffff' }}
              name="Open Deals in the Period"
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
};

export default TrendsChart;